import React, {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {Modal} from "../../system/modal";
import {getManualPayment, reviewManualPayment} from "../../../services/admin_service/manual_payment";
import {formatManualPaymentDate} from "./manualPaymentModel";

import {ManualPaymentDialogHeading, ManualPaymentNotice, ManualPaymentStatus} from "./ManualPaymentUI";

export function ManualPaymentReview({paymentId, onClose, onChanged}) {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState("");
  const [revision, setRevision] = useState(0);
  const inFlight = useRef(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getManualPayment(paymentId, {signal: controller.signal}).then(result => {
      if (active) setPayment(result);
    }).catch(cause => {
      if (active && cause?.code !== "ERR_CANCELED") {
        setPayment(null);
        setError(cause.message || "دریافت اطلاعات واریزی انجام نشد.");
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [paymentId, revision]);

  const review = async decision => {
    if (inFlight.current || loading || payment?.status !== "pending") return;
    if (decision === "reject" && !reason.trim()) {
      setError("برای رد واریزی، دلیل را وارد کنید.");
      return;
    }
    inFlight.current = true;
    setSubmitting(decision);
    setError("");
    try {
      await reviewManualPayment(paymentId, decision, decision === "reject" ? reason.trim() : "");
      toast.success(decision === "approve" ? "واریزی تایید شد." : "واریزی رد شد.");
      onChanged();
      onClose();
    } catch (cause) {
      setError(cause.message || "ثبت نتیجه بررسی انجام نشد.");
      if (cause.code === "already_reviewed") {
        try { setPayment(await getManualPayment(paymentId)); } catch { setPayment(null); }
        onChanged();
      }
    } finally {
      inFlight.current = false;
      setSubmitting("");
    }
  };

  return (
    <Modal open setOpen={() => { if (!inFlight.current) onClose(); }} size="md" hideFooter>
      {{
        header: <ManualPaymentDialogHeading title={`بررسی واریزی #${paymentId.toLocaleString("fa-IR")}`} subtitle="اطلاعات واریزی و نتیجه بررسی بانکی" onClose={onClose} disabled={Boolean(submitting)}/>,
        body: (
          <div dir="rtl" className="manual-payment-dialog">
            {loading ? <p role="status" className="manual-payment-state">در حال دریافت اطلاعات واریزی…</p> : payment && <>
              <div className="manual-payment-review-summary">
                <div><p>مبلغ واریزی</p><strong className="manual-payment-amount">{Number(payment.amount).toLocaleString("fa-IR")}<small>ریال</small></strong></div>
                <ManualPaymentStatus status={payment.status}/>
              </div>
              <dl className="manual-payment-details">
                <div><dt>مشتری</dt><dd>{payment.customer_name || "—"}<div className="manual-payment-help"><bdi>{payment.customer_phone}</bdi></div></dd></div>
                <div><dt>عامل</dt><dd>{payment.submitted_by_name || "—"}</dd></div>
                <div><dt>حساب مقصد</dt><dd>{payment.destination?.title || "—"}<div className="manual-payment-help">{payment.destination?.bank_name}</div></dd></div>
                <div><dt>تاریخ ثبت</dt><dd>{formatManualPaymentDate(payment.created_at)}</dd></div>
                <div className="is-wide"><dt>کارت / حساب / شبا</dt><dd dir="ltr" className="text-right">{[payment.destination?.card_number, payment.destination?.account_number, payment.destination?.sheba].filter(Boolean).join(" / ") || "—"}</dd></div>
                <div className="is-wide"><dt>شماره رسید یا مرجع</dt><dd>{payment.manual_reference || "—"}</dd></div>
                <div className="is-wide"><dt>توضیحات</dt><dd className="whitespace-pre-wrap">{payment.description || "—"}</dd></div>
              </dl>
              {payment.status === "pending" ? <>
                <ManualPaymentNotice>پس از بررسی استعلام بانکی، مبلغ و حساب مقصد، نتیجه را ثبت کنید. تایید این واریزی باعث افزایش موجودی مشتری می‌شود.</ManualPaymentNotice>
                <label className="manual-payment-label">دلیل رد واریزی
                  <textarea value={reason} onChange={event => setReason(event.target.value)} maxLength={1000} placeholder="در صورت رد واریزی، دلیل را برای کارشناس بنویسید." disabled={Boolean(submitting)} className="manual-payment-field"/>
                </label>
                <div className="manual-payment-dialog-actions">
                  <button type="button" onClick={() => review("reject")} disabled={Boolean(submitting)} className="manual-payment-button is-danger">{submitting === "reject" ? "در حال ثبت…" : "رد واریزی"}</button>
                  <button type="button" onClick={() => review("approve")} disabled={Boolean(submitting)} className="manual-payment-button is-primary">{submitting === "approve" ? "در حال ثبت…" : "تایید و ثبت پرداخت موفق"}</button>
                </div>
              </> : <p className="manual-payment-reviewed">این واریزی توسط {payment.reviewed_by_name || "ادمین"} در {formatManualPaymentDate(payment.reviewed_at)} بررسی شده است.{payment.rejection_reason && ` دلیل رد: ${payment.rejection_reason}`}</p>}
            </>}
            {error && <div role="alert" className="manual-payment-error">{error}{!payment && !loading && <button type="button" onClick={() => setRevision(value => value + 1)} className="mr-2 underline">دریافت دوباره اطلاعات</button>}</div>}
            <div className="manual-payment-dialog-actions"><button type="button" onClick={onClose} disabled={Boolean(submitting)} className="manual-payment-button">بستن</button></div>
          </div>
        ),
      }}
    </Modal>
  );
}
